import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFilteredUsers, putWorkspace } from '../actions/workspaceAction';
import {
    Grid2, Box, Typography, Modal, Avatar, Autocomplete, Paper, Snackbar, Alert, Select, MenuItem
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddButton from "../themes/overrides/AddButton";
import ModalBox from "../themes/overrides/ModalBox";
import TextFieldInput from "../themes/overrides/TextFieldInput";
import ProfileAddIcon from "../assets/images/profile-add.svg";
import copyLinkIcon from "../assets/images/copy-link.svg";
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { ReactComponent as RemoveIcon } from '../assets/images/icons/remove_icon.svg'
import PropTypes from 'prop-types';
import OutlinedAddButton from '../themes/overrides/OutlinedAddButton';

const ManageUsersModal = ({ open, onClose, selectedType, onSuccess }) => {
    const dispatch = useDispatch();
    const [currentType, setCurrentType] = useState(selectedType);
    const [emailInput, setEmailInput] = useState('');
    const filteredUsers = useSelector((state) => state.workspace.filteredUsers);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedDeleteUser, setSelectedDeleteUser] = useState([]);
    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedRoles, setSelectedRoles] = useState({});

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);

    useEffect(() => {
        if (selectedType) {
            setCurrentType(currentWorkspace?.data);
        }
    }, [currentWorkspace, selectedType]);


    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    };

    const handleEmailSearch = debounce((value) => {
        if (value.trim()) {
            dispatch(getFilteredUsers(value));
        }
    }, 300);

    const handleEmailChange = (event, newValue) => {
        setSelectedUsers(newValue);
        const newRoles = {};
        newValue.forEach(user => {
            if (!selectedRoles[user._id]) {
                newRoles[user._id] = 'viewer';
            }
        });
        setSelectedRoles(prev => ({ ...prev, ...newRoles }));
    };

    const handleInputChange = (event, newInputValue) => {
        setEmailInput(newInputValue);
        handleEmailSearch(newInputValue);
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            const userData = {
                user: userId,
                role: newRole
            };
            const workspaceId = currentType._id;
            const type = 'updateRole'; 
            await dispatch(putWorkspace(workspaceId, userData, type))
                .then(() => {
                    onSuccess?.();
                    setCurrentType(prev => ({
                        ...prev,
                        user_details: prev.user_details.map(user =>
                            user._id === userId
                                ? { ...user, role: { ...user.role, role: newRole } }
                                : user
                        )
                    }));
                    showSnackbar('User role updated successfully!', 'success');
                });
        } catch (error) {
            showSnackbar('Failed to update user role', 'error');
        }
    };



    const handleRemoveUserPopup = (user) => {
        setSelectedDeleteUser([user]);
        setDeleteModalOpen(true);
    }

    const handleAddUsers = async () => {
        try {
            const userData = selectedUsers.map(user => ({
                user: user._id,
                role: selectedRoles[user._id]
            }))
            const workspaceId = currentType._id;
            const type = 'addUsers'
            await dispatch(putWorkspace(workspaceId, userData, type))
                .then(() => {
                    onSuccess?.();
                    setSelectedUsers([]);
                    setSelectedRoles({});
                    showSnackbar('User added to workspace successfully!', 'success');
                    //onClose();
                })
        } catch (error) {
            showSnackbar('Failed to add user to workspace', 'error');
            console.error('Failed to add users, please try again')
        }
    };

    const handleDeleteUsers = async () => {
        try {
            const userIds = selectedDeleteUser.map(user => user._id);
            const workspaceId = currentType._id;
            const type = 'removeUsers'
            await dispatch(putWorkspace(workspaceId, userIds, type))
                .then(() => {
                    setSelectedDeleteUser([]);
                    setDeleteModalOpen(false);
                    onSuccess?.();
                    showSnackbar('User removed from workspace successfully!', 'success');
                })
        } catch (error) {
            showSnackbar('Failed to remove user from the workspace', 'error');
            console.error('Failed to remove users, please try again')
        }
    }

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const CustomChip = ({ email, user, onRoleChange, onDelete }) => (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                m: 0.25,
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '16px',
                height: '32px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    borderColor: '#bdbdbd'
                }
            }}
        >
            <Typography
                sx={{
                    ml: 1.5,
                    mr: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500
                }}
            >
                {email}
            </Typography>
            <Box
                component="span"
                onClick={(e) => e.stopPropagation()}
                sx={{
                    mr: 0.5,
                    position: 'relative',
                }}
            >
                <Select
                    value={selectedRoles[user._id]}
                    onChange={(e) => onRoleChange(user._id, e.target.value)}
                    style={{
                        fontSize: '0.813rem',
                        cursor: 'pointer',
                        padding: '2px 1px 2px 8px',
                        color: '#616161',
                        fontWeight: 500,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                ml: 2
                            }
                        }
                    }}
                >
                    <MenuItem
                        value="viewer"
                        sx={{
                            fontSize: '0.813rem',
                            minHeight: '28px',
                            color: '#757575',
                            py: 0.5
                        }}
                    >
                        View
                    </MenuItem>
                    <MenuItem
                        value="editor"
                        sx={{
                            fontSize: '0.813rem',
                            minHeight: '28px',
                            color: '#757575',
                            py: 0.5
                        }}
                    >
                        Edit
                    </MenuItem>
                </Select>
            </Box>
            <Box
                component="span"
                onClick={onDelete}
                sx={{
                    cursor: 'pointer',
                    p: '4px 8px',
                    mr: 0.5,
                    color: '#757575',
                    fontSize: '1rem',
                    lineHeight: 1,
                    transition: 'all 0.2s ease',
                    borderRadius: '50%',
                    '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        color: '#424242'
                    }
                }}
            >
                ×
            </Box>
        </Box>
    );

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <ModalBox sx={{ width: '560px', maxWidth: '95%' }}>
                    <Typography variant="h2" sx={{ mb: 1 }}>
                        Manage Users
                    </Typography>
                    <Grid2>
                        <Typography sx={{ mt: isMobile ? 2 : 6 }}>
                            <span className="font-normal">Invite people to workspace </span>
                            "{currentType?.name}"
                        </Typography>
                        <Grid2>
                            <Autocomplete
                                sx={{
                                    mt: 2,
                                    '& .MuiOutlinedInput-root': { padding: '6px' }
                                }}
                                multiple
                                value={selectedUsers}
                                onChange={handleEmailChange}
                                inputValue={emailInput}
                                onInputChange={handleInputChange}
                                options={
                                    filteredUsers?.filter(user => {
                                        const excludedIds = [
                                            currentType?.createdBy?.[0]?._id,
                                            ...(currentType?.user_details?.map(user => user._id) || [])
                                        ].filter(Boolean);

                                        return !excludedIds.includes(user._id);
                                    }) || []
                                }
                                noOptionsText="No user found"
                                getOptionLabel={(option) => option.email}
                                getOptionDisabled={(option) =>
                                    selectedUsers.some(user => user.email === option.email)
                                }
                                popupIcon={null}
                                renderInput={(params) => (
                                    <TextFieldInput
                                        {...params}
                                        label="Add people with their e-mail address"
                                        placeholder="Type to search..."
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { onDelete } = getTagProps({ index });
                                        return (
                                            <CustomChip
                                                key={option.email}
                                                email={option.email}
                                                user={option}
                                                role={selectedRoles[option._id] || 'viewer'}
                                                onRoleChange={(userId, newRole) => {
                                                    setSelectedRoles(prev => ({
                                                        ...prev,
                                                        [userId]: newRole
                                                    }));
                                                }}
                                                onDelete={onDelete}
                                            />
                                        );
                                    })
                                }
                                PaperComponent={({ children }) => (
                                    <Paper sx={{
                                        backgroundColor: "#ECECEC",
                                        borderRadius: "10px",
                                        mt: 1,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                    }}>{children}</Paper>
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                py: 1,
                                            }}
                                        >
                                            <Avatar
                                                alt={option.name}
                                                src={option.avatarUrl}
                                                sx={{ width: 36, height: 36 }}
                                            />
                                            <Box sx={{ ml: 2 }}>
                                                <Typography
                                                    margin="dense"
                                                    sx={{
                                                        lineHeight: 1.2,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {option.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        lineHeight: 1,
                                                        display: 'block'
                                                    }}
                                                >
                                                    {option.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            />
                        </Grid2>
                        <Box sx={{ mt: 4, display: isMobile ? 'block' : 'flex', justifyContent: 'space-between' }}>
                            <AddButton
                                startIcon={
                                    <img
                                        src={ProfileAddIcon}
                                        alt="Provide access"
                                        style={{ width: 24, height: 24 }}
                                    />
                                }
                                label="Provide access"
                                sx={{
                                    fontSize: '16px',
                                    '&.Mui-disabled': {
                                        background: '#ACACAC',
                                        color: '#FFFFFF'
                                    },
                                    width: isMobile ? '100%' : '220px'
                                }}
                                disabled={selectedUsers.length === 0}
                                onClick={handleAddUsers}
                            />
                            <OutlinedAddButton
                                startIcon={
                                    <img
                                        src={copyLinkIcon}
                                        alt="Copy Link Icon"
                                        style={{ width: 24, height: 24 }}
                                    />
                                }
                                color="white"
                                label="Copy link"
                                sx={{ fontSize: '16px', border: "1.5px solid", mt: isMobile ? 1 : 0, width: isMobile ? '100%' : '175px' }}
                                onClick={onClose}
                            />
                        </Box>
                        <Typography sx={{ mt: 6 }}>
                            <span className="font-normal">People with access </span>
                        </Typography>
                        <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto' }}>
                            {[...currentType?.user_details || []]
                                .sort((a, b) => {
                                    if (a.role?.role === 'admin') return -1;
                                    if (b.role?.role === 'admin') return 1;
                                    return 0;
                                })
                                .map((user, index) => (
                                    <Grid2
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            py: 1,
                                            width: isMobile ? '400px' : '100%'
                                        }}
                                    >
                                        <Avatar
                                            alt={user.name}
                                            src={user.avatarUrl}
                                            sx={{ width: 36, height: 36 }}
                                        />
                                        <Box sx={{ ml: 2, flexGrow: 1 }}>
                                            <Typography margin="dense" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                                                {user.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ lineHeight: 1, display: 'block' }}>
                                                {user.email}
                                            </Typography>
                                        </Box>
                                        {user.role?.role === 'admin' ? (
                                            <Typography sx={{ fontSize: "12px", mr: 4 }}>
                                                {user.role.role}
                                            </Typography>
                                        ) : (
                                            <Select
                                                value={user.role?.role || 'viewer'}
                                                onChange={(e) => handleRoleChange(user._id, e.target.value)}
                                                sx={{
                                                    mr: 2,
                                                    fontSize: '12px',
                                                    '& .MuiSelect-select': { py: 0.5, px: 1 }
                                                }}
                                            >
                                                <MenuItem
                                                    value="viewer"
                                                    sx={{
                                                        fontSize: '0.813rem',
                                                        minHeight: '28px',
                                                        color: '#757575',
                                                        py: 0.5
                                                    }}
                                                >
                                                    View
                                                </MenuItem>
                                                <MenuItem
                                                    value="editor"
                                                    sx={{
                                                        fontSize: '0.813rem',
                                                        minHeight: '28px',
                                                        color: '#757575',
                                                        py: 0.5
                                                    }}
                                                >
                                                    Edit
                                                </MenuItem>
                                            </Select>
                                        )}
                                        <Typography
                                            onClick={() => user.role?.role !== 'admin' && handleRemoveUserPopup(user)}
                                            sx={{
                                                fontSize: "12px",
                                                color: user.role?.role === 'admin' ? '#ACACAC' : '#960101',
                                                cursor: user.role?.role === 'admin' ? 'not-allowed' : 'pointer',
                                                pointerEvents: user.role?.role === 'admin' ? 'none' : 'auto'
                                            }}
                                        >
                                            Remove
                                        </Typography>
                                    </Grid2>
                                ))}
                        </Box>
                    </Grid2>
                    <DeleteConfirmationModal
                        startIcon={<RemoveIcon />}
                        deleteButtonLabel={'Remove'}
                        open={deleteModalOpen}
                        onClose={() => setDeleteModalOpen(false)}
                        text={
                            <>
                                <span className="font-normal">Are you sure, you want to remove </span>
                                {`"${selectedDeleteUser[0]?.name}" `}<span className="font-normal"> from the workspace</span>
                                {` "${currentType?.name}" `}
                                ?
                            </>
                        }
                        onConfirm={handleDeleteUsers}
                    />
                </ModalBox>
            </Modal>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >{snackbarMessage}</Alert>
            </Snackbar>
        </>
    );
};

// ManageUsersModal.propTypes = {
//     open: PropTypes.bool,
//     onClose: PropTypes.func,
//     selectedType: PropTypes.any,
//     onSuccess: PropTypes.func
// };


export default ManageUsersModal;